/**
 * Crash sequence — 3rd-person crash cinematic + red vignette (Stage 2 + Thomas
 * review-fix).
 *
 * On the `crash` sim event:
 *  - a red vignette DOM overlay fades in fast and persists through the
 *    'crashed' → 'gameover' transition (unchanged Stage 2 behavior).
 *  - the ScreenShake's sustained tumble is triggered (screenShake.ts).
 *  - the camera INSTANTLY cuts to a 3rd-person crash cam: detached from the
 *    FP rig (CameraRig.setCrashCam(true)), placed behind-above-side of the
 *    impact so it NEVER sits inside the crash vehicle. From that cut pose it
 *    DOLLIES out over 0.6 s to ONE fixed world-space pose (higher, further
 *    back, offset toward the road centre) and then holds perfectly still. The
 *    lookAt is a fixed world point for the whole cinematic — no pan, no orbit,
 *    no tracking of the ragdoll.
 *  - the cyclist (a clone of registry.cyclist, ~1.9 m) is shown at the pose of
 *    the sim's REAL physics body: the sim spawns a dynamic Rapier rounded box on
 *    crash (src/sim/world.ts) and keeps stepping the physics world through the
 *    'crashed' phase, mirroring the pose into `state.crashCyclist`. Render just
 *    copies that pose — no analytic arc, no ground clamp hack, so the rider can
 *    never clip through the road. Once the body settles, the MODEL orientation
 *    eases to flat-on-road (roll 90°) at a lying rest height; the physics body
 *    is untouched. The camera never moves for it.
 *  - the crash vehicle keeps driving (the sim only idles the phase; vehicles
 *    keep moving) — we never freeze the world visuals.
 *
 * The cinematic must complete within the sim's 'crashed' phase (CRASH_TO_GAMEOVER
 * = 2.4 s in world.ts). The vignette is a radial-gradient DOM element inside
 * the 9:16 viewport.
 *
 * Zero per-frame allocation: the pose + camera math reuses scratch objects.
 */

import {
  Box3,
  Euler,
  Group,
  Quaternion,
  Vector3,
  type Material,
  type Mesh,
  type PerspectiveCamera,
  type Scene,
  type Object3D,
} from "three/webgpu";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/addons/libs/meshopt_decoder.module.js";
import { JUICE, clamp01 } from "./juice";
import { transformedBoxMinY, groundLiftY } from "./groundClamp";
import type { CameraRig } from "./cameraRig";
import type { WorldState } from "../sim/types";

const CR = JUICE.crash;

/**
 * Multi-pass crash-cinematic warm-up handle (see {@link CrashSequence.warmup}).
 * Mirrors the vehicle / biome / leg handles: the caller runs `passes`
 * iterations of `beginPass(p)` + one real frame, then `restore`.
 */
export interface CrashWarmup {
  /** Number of passes the caller must render (>= 1). */
  readonly passes: number;
  /** Pose the crash cyclist for pass `p` (0-based). */
  beginPass(p: number): void;
  /** Hide the crash cyclist again and hand it back to the cinematic. */
  restore(): void;
}

// --- Crash cam: instant cut, then ONE dolly-out to a FIXED pose -----------
//
// Owner call: no panning, no orbiting, no tracking of the tumbling ragdoll.
// The camera cuts (instantly, so it is never inside the crash vehicle for even
// one frame) to a pose beside/above/behind the impact, DOLLIES out over
// CAM_DOLLY_TIME to a single world-space pose chosen once at impact, and then
// holds perfectly still. The lookAt target is a fixed world point for the whole
// cinematic — the ragdoll stays in frame because the sim's containment walls
// keep it on the road, not because the camera chases it.
//
// Cut pose (dolly START) — the old immediate-cut placement, signed to the side
// the crash vehicle came from.
const CAM_CUT_X = 2.5; // m — lateral (signed per crash side)
const CAM_CUT_Y = 2.0; // m — above the impact
const CAM_CUT_Z = 4.0; // m — behind the player (+Z = behind, player travels -Z)
// Final pose (dolly END) — pulled back and up, offset toward the ROAD CENTRE so
// the shot always contains asphalt even when the crash happens on the shoulder.
//
// Z is set from the SIM's ragdoll arc, not by eye: the thrown rider is launched
// back toward the camera (+Z) and comes to rest ~5 m behind the impact point
// (sim/world.ts CYC_LAUNCH_BACK), peaking ~2.4 m up around z = 3. A camera at
// the impact point's usual 4 m stand-off would be INSIDE that arc — the rider
// flies over and past it and the shot ends on empty road. 11 m keeps the whole
// tumble in front of the lens for the entire 2.4 s cinematic.
//
// 2026-08-02: MEASURED, then left alone. The sim's throw is lower now
// (CYC_LAUNCH_UP 8.0 -> 5.5), so the lens can physically come in, and pulling
// it in is the only lever this module has on the cyclist's ALIASING (the bike's
// spokes are sub-pixel thin at this range). It is not worth it: at z = 9.5 the
// settled rider is already at NDC x 0.73 and at z = 8 he leaves the frame
// entirely (NDC x 1.00, measured over 5 crashes), for a ~16% gain in pixels
// across the bike. The aliasing fix belongs in the ASSET — see the matte-out
// note in attachCinematic.
const CAM_FINAL_X = 1.8; // m — lateral, signed toward road centre
const CAM_FINAL_Y = 4.0; // m
const CAM_FINAL_Z = 11.0; // m behind the impact (see above)
/** Seconds of ease-out dolly from the cut pose to the final pose. */
const CAM_DOLLY_TIME = 0.6;
/**
 * The fixed lookAt x: the impact point, biased toward the side the rider is
 * thrown (the crash side) so the tumble sits INSIDE the frame rather than
 * against its edge, then clamped toward the road centre so the shot always
 * contains asphalt even for a crash on the shoulder.
 */
const CAM_LOOK_X_BIAS = 1.0; // m toward the launch side
const CAM_LOOK_X_CLAMP = 2.5; // m from road centre
const CAM_LOOK_Y = 1.2; // m — roughly the fallen rider / road surface
/** ...pushed a little BACK from the impact, to the middle of the tumble. */
const CAM_LOOK_Z = 3.0; // m

// --- Cyclist render pose ---------------------------------------------------
// The cyclist GLB pivot is its bbox center (~1.9 m model, length axis Z), so
// upright half-height ≈ 0.95 m. The sim's rounded box has a 0.72 m long
// half-extent and its center can never sit below its own 0.34 m short
// half-extent, so we bias the rendered Y up to a floor that keeps the (mostly
// tumbling / eventually lying) model clear of the road.
const CYCLIST_HALF_H = 0.95;
/** Rest height of the model center once lying flat on the road (pre-clamp). */
const REST_Y = 0.35;
/** Exponential rate (1/s) at which the settled model eases to the flat pose. */
const SETTLE_EASE_K = 6.0;
/**
 * Road surface Y + the clearance kept under the model's lowest point.
 *
 * The sim's rounded box is SHORTER and THINNER than the cyclist GLB, so posing
 * the model at the body's centre + rotation
 * buries the model's extremities in the asphalt — both mid-tumble and in the
 * settled lying pose (whose REST_Y was a hand-tuned guess). Instead of biasing
 * the centre Y by a constant, every frame we measure the model's actual
 * world-space bbox bottom and lift it clear (see groundClamp.ts).
 */
const GROUND_Y = 0;
const GROUND_EPS = 0.01;

/**
 * Crash-sequence controller. Construct with the HUD root (9:16 viewport).
 * Call `attachCinematic` after the scene + camera rig + cyclist GLB are ready
 * to enable the 3rd-person cinematic. Call `trigger()` from the crash event
 * handler; call `update(dt, phase)` each render frame to drive the vignette
 * fade + cinematic; call `reset()` on restart.
 */
export class CrashSequence {
  private readonly root: HTMLDivElement;
  private readonly vignette: HTMLDivElement;
  private opacity = 0;
  private active = false;

  // --- 3rd-person cinematic state (null until attachCinematic) ---
  private scene: Scene | null = null;
  private rig: CameraRig | null = null;
  /** Cyclist clone shown during the crash; hidden otherwise. */
  private cyclist: Group | null = null;
  /** Materials this class cloned (matte-out pass) and therefore owns. */
  private readonly materials: Material[] = [];
  /** Whether the 3D cinematic is wired (scene + rig + cyclist all present). */
  private cinematicReady = false;

  // Per-crash dynamic state (zeroed in reset / set on trigger).
  private crashElapsed = 0;
  /** Lateral sign of the crash vehicle relative to player (+1 / -1). */
  private crashSide = 1;
  /** Last rendered cyclist position (mirrored from the sim body, Y-biased). */
  private cycPos = new Vector3();
  /** Live camera position (dollied from camCutPos to camFinalPos, then held). */
  private camPos = new Vector3();
  /** FIXED world-space lookAt for the whole cinematic (never re-aimed). */
  private camLook = new Vector3();
  /** Dolly endpoints, both chosen once at impact. */
  private readonly camCutPos = new Vector3();
  private readonly camFinalPos = new Vector3();
  /** Reusable scratch (zero per-frame allocation). */
  private readonly _tmp = new Vector3();
  private readonly _bodyQuat = new Quaternion();
  private readonly _restQuat = new Quaternion();
  private readonly _restEuler = new Euler();
  /**
   * The crash cyclist's bbox in its OWN local frame (no position/rotation, but
   * including the model's own scale), measured once in attachCinematic. Used
   * every cinematic frame to keep the posed model above the road.
   */
  private readonly localBox = new Box3();
  /** Whether localBox holds a valid measurement. */
  private hasLocalBox = false;

  constructor(root: HTMLDivElement) {
    this.root = root;
    this.vignette = document.createElement("div");
    Object.assign(this.vignette.style, {
      position: "absolute",
      inset: "0",
      pointerEvents: "none",
      opacity: "0",
      background:
        "radial-gradient(ellipse at center, transparent 35%, rgba(180,20,20,0.85) 100%)",
    } as Partial<CSSStyleDeclaration>);
    this.root.appendChild(this.vignette);
  }

  /**
   * Wire the 3rd-person cinematic. Call after the camera rig + scene exist.
   * `cyclistGlb` is registry.cyclist if the caller has it (preferred — shares
   * the already-loaded GLB); if null, we load cyclist.glb again with the same
   * GLTFLoader + meshopt pattern (one extra fetch, served from cache). Either
   * way the crash sequence owns its own clone. If the cyclist can't be
   * obtained the cinematic is skipped (the vignette still works) — graceful
   * degrade.
   */
  async attachCinematic(scene: Scene, rig: CameraRig, cyclistGlb: Group | null): Promise<void> {
    this.scene = scene;
    this.rig = rig;
    let source = cyclistGlb;
    if (!source) {
      // Fallback: load cyclist.glb directly (the registry cyclist isn't
      // reachable without a main.ts wiring change). Same loader pattern as
      // assets.ts / kickLeg.ts; one extra fetch, cached by the browser.
      try {
        source = await this.loadCyclist();
      } catch (err) {
        console.warn("Crash cinematic: cyclist.glb failed to load (non-fatal):", err);
        source = null;
      }
    }
    if (source) {
      // Clone the source GLB (shares GPU geometry/materials with the registry
      // owner — no extra GPU cost beyond the instance).
      const clone = source.clone(true);
      clone.name = "crashCyclist";
      clone.visible = false;
      clone.frustumCulled = false; // it tumbles; bounds change every frame
      // The sun casts real shadows (render/riderCaster.ts); the fallen rider
      // gets one too, which grounds the 3rd-person shot.
      //
      // WHY THIS WORKS DURING THE CINEMATIC (verified 2026-08-02 against the
      // owner note "doesn't seem like the cyclist crash has shadow"): the sun
      // and its ortho frustum are parented to the CAMERA RIG ROOT, and
      // CameraRig.setCrashCam parks that root at the world origin with an
      // identity rotation for the whole cinematic. The frustum's rig-frame box
      // (riderCaster SHADOW_BOX: x ±15, z −70..+20) is therefore the WORLD box
      // during a crash, and the sim's containment walls keep the thrown rider
      // inside x ±4.6 / z ≲ +6 — comfortably inside it. Confirmed by A/B: with
      // this clone hidden the diagonal shadow on the asphalt disappears.
      //
      // receiveShadow as well as cast: the rider tumbles through the shadows of
      // the truck it hit and of the roadside buildings, and once it settles the
      // bike's own frame shadows the rider lying under it. Without it the
      // fallen cyclist is the one fully-lit object in a shot full of shadows.
      clone.traverse((o) => {
        const m = o as Mesh;
        if (!m.isMesh) return;
        m.castShadow = true;
        m.receiveShadow = true;
        m.frustumCulled = false;
        // MATTE-OUT (2026-08-02 aliasing pass). `clone(true)` SHARES materials
        // with the registry cyclist, so they are cloned here before being
        // touched — the rider-shadow caster clones the same source.
        //
        // ROOT CAUSE of the owner's "the cyclist has heavy aliasing, everything
        // else is anti-aliased": cyclist.glb models the bicycle with real spokes
        // and a real rim, ~0.5 DEVICE PIXELS wide at crash-cam range, while
        // every other object in the scene is chunky low-poly with metre-scale
        // silhouettes. It is a sampling-rate problem, not a renderer one — the
        // renderer is already MSAA 4x at DPR 2 (verified: renderer.samples ===
        // 4), and the same model is perfectly smooth when the camera is moved
        // to 3 m. The real fix is to re-author those wheels chunky; until then
        // this removes the WORSE half of the artifact: a specular lobe riding
        // on sub-pixel tubes turns steady jaggies into crawling sparkle.
        const matte = (mat: Material): Material => {
          const c = mat.clone() as Material & {
            roughness?: number;
            clearcoat?: number;
            clearcoatRoughness?: number;
          };
          c.roughness = 1;
          c.clearcoat = 0;
          c.clearcoatRoughness = 1;
          this.materials.push(c);
          return c;
        };
        const src = m.material as Material | Material[];
        m.material = Array.isArray(src) ? src.map(matte) : matte(src);
      });
      scene.add(clone);
      this.cyclist = clone;
      this.measureLocalBox(clone);
    }
    this.cinematicReady = !!(scene && rig && this.cyclist);
  }

  /**
   * Measure the clone's bbox in its own local frame: neutralise its transform
   * (Box3.setFromObject walks world matrices, so an inherited pose or scale
   * would be baked in and then applied a SECOND time by the per-frame clamp),
   * measure, then restore. Called once, at attach.
   */
  private measureLocalBox(clone: Group): void {
    const pos = clone.position.clone();
    const quat = clone.quaternion.clone();
    const scale = clone.scale.clone();
    clone.position.set(0, 0, 0);
    clone.quaternion.identity();
    clone.scale.set(1, 1, 1);
    clone.updateWorldMatrix(false, true);
    this.localBox.setFromObject(clone, true);
    clone.position.copy(pos);
    clone.quaternion.copy(quat);
    clone.scale.copy(scale);
    clone.updateWorldMatrix(false, true);
    this.hasLocalBox = !this.localBox.isEmpty();
    if (!this.hasLocalBox) {
      console.warn("Crash cinematic: cyclist bbox is empty — ground clamp disabled");
    }
  }

  /**
   * Push the posed cyclist up until its lowest point clears the road, and
   * report the applied lift. The model is longer/wider than the sim capsule
   * whose pose it mirrors, so without this its extremities interpenetrate the
   * asphalt during the tumble and in the settled lying pose.
   *
   * Only the clone's OWN world matrix is needed (localBox is expressed in the
   * clone's local frame), so this refreshes just that matrix; the renderer
   * recomputes the full hierarchy before drawing anyway.
   */
  private clampToGround(c: Group): number {
    if (!this.hasLocalBox) return 0;
    c.updateMatrix();
    if (c.parent) {
      c.matrixWorld.multiplyMatrices(c.parent.matrixWorld, c.matrix);
    } else {
      c.matrixWorld.copy(c.matrix);
    }
    const minY = transformedBoxMinY(this.localBox, c.matrixWorld.elements);
    const lift = groundLiftY(minY, GROUND_Y, GROUND_EPS);
    if (lift > 0) {
      c.position.y += lift;
      c.updateMatrix();
      if (c.parent) {
        c.matrixWorld.multiplyMatrices(c.parent.matrixWorld, c.matrix);
      } else {
        c.matrixWorld.copy(c.matrix);
      }
    }
    return lift;
  }

  /**
   * PIPELINE WARM-UP (2026-08-02 crash-hitch pass). Park the crash cyclist in
   * the scene, VISIBLE, so the load-screen warm-up draws it once and its
   * pipelines are compiled there instead of on the frame the player crashes.
   *
   * WHY IT IS NEEDED. `attachCinematic` clones the registry cyclist and CLONES
   * ITS MATERIALS (the matte-out pass above), then leaves the clone
   * `visible = false` until the first crash. A cloned material is a brand-new
   * material as far as three's render-object cache is concerned, and an
   * invisible object is skipped by both `renderer.compileAsync` and the draw —
   * so the FIRST crash of a page load compiled the clone's colour pipeline AND
   * its shadow pipeline inside `renderer.render`. Measured on the bench over 5
   * fresh loads: 2 compiles (`renderPipeline_MeshPhysicalMaterial_*` +
   * `renderPipeline_ShadowMaterial_*`) and a 32-42 ms crash frame, against
   * ~7 ms for the SECOND crash of the same page load. That is exactly the
   * owner's "it froze, then showed the aftermath".
   *
   * Warming it needs nothing but visibility: a pipeline is keyed by material +
   * geometry layout + pass, not by pose. The clone keeps its matte materials
   * and its `castShadow`/`receiveShadow` flags, so the pipelines compiled here
   * are bit-for-bit the ones the cinematic draws.
   *
   * @param x,y,z where to park it (the caller's shared warm-up spot, which is
   *              inside both the camera frustum and the shadow frustum).
   * @returns null if the cinematic never attached (GLB load failure) — the
   *          caller treats that as "nothing to warm".
   */
  warmup(x: number, y: number, z: number): CrashWarmup | null {
    const c = this.cyclist;
    if (!c) return null;
    const visible = c.visible;
    const pos = c.position.clone();
    const quat = c.quaternion.clone();
    return {
      passes: 1,
      beginPass: () => {
        c.visible = true;
        c.position.set(x, y, z);
        c.quaternion.identity();
        c.updateMatrixWorld(true);
      },
      restore: () => {
        c.visible = visible;
        c.position.copy(pos);
        c.quaternion.copy(quat);
        c.updateMatrixWorld(true);
      },
    };
  }

  /** Load cyclist.glb (meshopt-compressed) — the fallback path. */
  private loadCyclist(): Promise<Group> {
    const loader = new GLTFLoader();
    loader.setMeshoptDecoder(MeshoptDecoder);
    return new Promise<Group>((resolve, reject) => {
      loader.load(
        "/assets/models/cyclist.glb",
        (gltf) => resolve(gltf.scene as Group),
        undefined,
        (err) => reject(err),
      );
    });
  }
  /**
   * Trigger the crash vignette (called from the crash event handler). The
   * 3rd-person cinematic is started separately via `startCinematic` once the
   * crash vehicle's side is known (resolved in the render update).
   */
  trigger(): void {
    this.active = true;
  }

  /**
   * Start the 3rd-person cinematic. Called from the render update once the
   * crash vehicle's lateral offset is known. `vehicleDx` = crash vehicle x
   * minus player x (sign picks the launch/camera side); `playerX` is the
   * player's lateral position at impact. No-op if the cinematic isn't wired.
   */
  startCinematic(vehicleDx: number, playerX: number): void {
    if (!this.cinematicReady) return;

    this.crashElapsed = 0;
    // Launch side: the cyclist flies toward the vehicle's side; if the vehicle
    // is dead-center, default to +1 (right).
    this.crashSide = vehicleDx >= 0 ? 1 : -1;

    // --- Cyclist: start at the player position, on the bike (upright). The
    //     pose is owned by the sim's physics body from the next frame on.
    this.cycPos.set(playerX, CYCLIST_HALF_H, 0);

    // --- Camera: INSTANT cut, then a single dolly-out to a FIXED pose ----
    // The cut guarantees the camera is never inside the crash vehicle for even
    // a single frame; it is also the dolly's start pose.
    this.camCutPos.set(
      playerX + CAM_CUT_X * this.crashSide,
      CAM_CUT_Y,
      CAM_CUT_Z,
    );
    // Final pose: offset toward the ROAD CENTRE (so a crash on the shoulder
    // still frames asphalt, not the verge), higher and further back.
    const centreSide = playerX >= 0 ? -1 : 1;
    this.camFinalPos.set(
      playerX + CAM_FINAL_X * centreSide,
      CAM_FINAL_Y,
      CAM_FINAL_Z,
    );
    this.camPos.copy(this.camCutPos);
    // Fixed lookAt for the WHOLE cinematic: the impact point, x pulled toward
    // the road centre so the framing always contains the road.
    const lookX = playerX + CAM_LOOK_X_BIAS * this.crashSide;
    this.camLook.set(
      Math.max(-CAM_LOOK_X_CLAMP, Math.min(CAM_LOOK_X_CLAMP, lookX)),
      CAM_LOOK_Y,
      CAM_LOOK_Z,
    );
    this.rig!.setCrashCam(true, { position: this.camPos, target: this.camLook });

    // --- Show the cyclist at the launch pose -----------------------------
    const c = this.cyclist!;
    c.visible = true;
    c.position.copy(this.cycPos);
    c.quaternion.identity();
    this.clampToGround(c);
    this.cycPos.y = c.position.y;
  }

  /**
   * Advance the vignette fade + the 3rd-person cinematic. Call each render
   * frame. The cinematic is anchored to the player position captured at crash
   * time (the player is frozen during the 'crashed' phase), so no per-frame
   * player position is needed.
   * @param dt    render-frame delta seconds.
   * @param state current sim state (phase + the crash cyclist physics pose).
   */
  update(dt: number, state: WorldState): void {
    const phase = state.phase;
    // --- Vignette (unchanged Stage 2 behavior) ---
    if (this.active) {
      const k = 1 - Math.exp(-1 / CR.vignetteFadeIn * dt);
      this.opacity += (CR.vignetteMaxOpacity - this.opacity) * k;
    } else if (phase !== "crashed" && phase !== "gameover") {
      const k = 1 - Math.exp(-8 * dt);
      this.opacity += (0 - this.opacity) * k;
    }
    this.vignette.style.opacity = clamp01(this.opacity).toFixed(3);

    // --- 3rd-person cinematic (only while the crashed phase is active) ---
    if (this.cinematicReady && (phase === "crashed" || (this.active && phase === "gameover"))) {
      this.stepCinematic(dt, state);
    }
  }

  /**
   * Mirror the sim's crash-cyclist physics body onto the model + ease the
   * camera. No integration happens here — the sim's Rapier body owns the
   * motion, which is why the rider can never sink through the road.
   */
  private stepCinematic(dt: number, state: WorldState): void {
    this.crashElapsed += dt;
    const c = this.cyclist!;
    const cam = this.rig!.camera as PerspectiveCamera;
    const body = state.crashCyclist;

    if (body) {
      // Position: raw physics capsule centre. The road clearance is enforced
      // AFTER the orientation is applied, by measuring the posed model's own
      // bbox (clampToGround) — a constant Y floor cannot work here because how
      // deep the model reaches below its centre depends entirely on how it is
      // currently rotated.
      this.cycPos.set(body.x, body.y, body.z);
      this._bodyQuat.set(body.quat[0], body.quat[1], body.quat[2], body.quat[3]);
      if (body.settled) {
        // Settled: ease the MODEL (not the body) to flat-on-road — keep the
        // body's yaw, zero the pitch, roll 90° about the length axis so the
        // bike + rider lie on their side — and drop to the lying rest height.
        this._restEuler.setFromQuaternion(this._bodyQuat, "YXZ");
        this._restEuler.set(0, this._restEuler.y, Math.PI / 2, "YXZ");
        this._restQuat.setFromEuler(this._restEuler);
        const k = 1 - Math.exp(-SETTLE_EASE_K * dt);
        c.quaternion.slerp(this._restQuat, k);
        this.cycPos.y += (REST_Y - this.cycPos.y) * k;
      } else {
        c.quaternion.copy(this._bodyQuat);
      }
      c.position.copy(this.cycPos);
      // Ground clamp — applies to BOTH the tumble and the settled flat pose.
      this.clampToGround(c);
      // Remember where the model actually ended up (the clamp may have lifted
      // it); the camera does NOT use this — the shot is fixed.
      this.cycPos.y = c.position.y;
    }

    // --- Camera: ONE dolly-out, then a dead-still hold ---------------------
    // Ease-out (cubic) from the cut pose to the fixed final pose over
    // CAM_DOLLY_TIME, then nothing moves at all. The look target never changes,
    // so there is no pan, no orbit and no tracking of the tumbling ragdoll —
    // it stays in frame because the sim contains it on the road.
    const t = clamp01(this.crashElapsed / CAM_DOLLY_TIME);
    const e = 1 - (1 - t) * (1 - t) * (1 - t);
    this.camPos.copy(this._tmp.copy(this.camFinalPos).sub(this.camCutPos).multiplyScalar(e))
      .add(this.camCutPos);

    // Write the camera pose (root is parked at the origin by the rig's crash
    // path, so camera.position/lookAt are world-space).
    cam.position.copy(this.camPos);
    cam.lookAt(this.camLook);
  }

  /** Reset on restart: hide the cyclist, restore FP control, clear state. */
  reset(): void {
    this.active = false;
    this.opacity = 0;
    this.vignette.style.opacity = "0";

    // Restore FP camera control.
    if (this.rig) {
      this.rig.setCrashCam(false);
    }
    // Hide + park the cyclist clone.
    if (this.cyclist) {
      this.cyclist.visible = false;
      this.cyclist.position.set(0, CYCLIST_HALF_H, 0);
      this.cyclist.quaternion.identity();
    }
    this.crashElapsed = 0;
    this.cycPos.set(0, CYCLIST_HALF_H, 0);
  }

  /** Whether the 3rd-person cinematic is wired and owns the camera. */
  get cinematicActive(): boolean {
    return this.cinematicReady && this.active;
  }

  dispose(): void {
    this.vignette.remove();
    if (this.cyclist && this.scene) {
      this.scene.remove(this.cyclist as unknown as Object3D);
    }
    // Geometry is shared with the registry cyclist — not ours to free.
    for (const m of this.materials) m.dispose();
    this.materials.length = 0;
  }
}
